import { useEffect } from 'react';
import { ButtonHeadless, Modal, Spin } from '@wajeez-web/components/ui';
import { SubmitHandler, useForm } from 'react-hook-form';
import { FormSelectInput } from '@wajeez-web/components/form-builder';
import { Server } from '@wajeez/wajeez.microservices';
import { ReturnTranslatedText } from '@wajeez-web/core/localization';
import {
  assignPromoCodeToUtilityUser,
  assignPublisherToUtilityUser,
  SalesActions,
  useAccountDispatch,
  useAccountSelector,
} from '@wajeez-web/stores/account';
import { BiLoaderAlt } from 'react-icons/bi';
import {
  useAssignPromoCodeToUtilityUser,
  useAssignPublisherToUtilityUser,
  useGetPromoCodeAliasesLookups,
  useGetPublishersLookups,
} from '@wajeez-web/components/helpers';
import { getPromoCodeAliasesLookups } from '@wajeez-web/stores/product';
import { getPublishersLookups } from '@wajeez-web/stores/content';

type Input = {
  publisherId: number;
};

interface AssignPublisherToUtilityUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit?: () => Promise<void> | void;
  saleUserId?: number | undefined;
}

export const AssignPublisherToUtilityUserModal = ({
  isOpen,
  onClose,
  onSubmit,
  saleUserId,
}: AssignPublisherToUtilityUserModalProps) => {
  const accountDispatch = useAccountDispatch();

  const assignPublisherToSaleUserState = useAssignPublisherToUtilityUser();

  const publishersLookups = useGetPublishersLookups();

  const saleUserById = useAccountSelector(({ sales }) => sales.utilityUserById);

  const { handleSubmit, control, reset } = useForm<Input>();

  const onFormSubmit: SubmitHandler<Input> = async (data) => {
    assignPublisherToUtilityUser(
      {
        utilityUserId: saleUserId,
        ...data,
      },
      onSubmit
    );
  };

  useEffect(() => {
    if (saleUserById.data?.publisherId) {
      reset({
        publisherId: Number(saleUserById.data.publisherId),
      });
    }
  }, [saleUserById.data?.publisherId, reset]);

  useEffect(() => {
    if (saleUserId) {
      accountDispatch(SalesActions.getUtilityUserByIdAsync(Number(saleUserId)));
    }

    getPublishersLookups();
  }, [saleUserId, accountDispatch]);

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      title="Sale User"
      alertTarget="modal"
    >
      <div className="w-full">
        {saleUserById.status === 'loading' ||
          publishersLookups.status === 'loading' ? (
          <div className="w-full min-h-[200px] flex justify-center items-center">
            <Spin />
          </div>
        ) : (
          <form
            onSubmit={handleSubmit(onFormSubmit)}
            noValidate
            className="w-full flex flex-col justify-start items-start"
          >
            <div className="w-[300px] flex flex-col justify-start items-start space-y-2">
              <FormSelectInput
                name="publisherId"
                control={control}
                fieldType={{
                  fieldType: Server.FieldType.Select,
                  fieldOptions: {
                    selectOptions:
                      publishersLookups.data?.map((p) => {
                        return {
                          value: p.id || '',
                          label: p.name || '',
                        };
                      }) || [],
                  },
                  required: true,
                  label: 'Publisher',
                }}
                errorMessages={{ required: 'This field is required' }}
                widthClassName="w-full"
                showErrors
              />
            </div>
            <div className="mt-6 w-full flex justify-end items-center space-s-2">
              <div className="w-[70px]">
                <ButtonHeadless
                  bgStyle="bg-warmGray-300"
                  borderStyle="border-none"
                  paddingYStyle="py-1"
                  action={onClose}
                >
                  <div className="text-warmGray-600 text-[16px] font-medium">
                    <ReturnTranslatedText
                      text="buttons.Cancel"
                      origintext="Cancel"
                    />
                  </div>
                </ButtonHeadless>
              </div>
              <div className="w-[70px]">
                <ButtonHeadless
                  type="submit"
                  borderStyle="border-none"
                  paddingYStyle="py-1"
                >
                  <div className="text-white text-[16px] font-medium flex justify-center items-center space-s-2">
                    <ReturnTranslatedText
                      text="buttons.Save"
                      origintext="Save"
                    />
                    {assignPublisherToSaleUserState.status === 'loading' && (
                      <BiLoaderAlt className="animate-spin w-5" />
                    )}
                  </div>
                </ButtonHeadless>
              </div>
            </div>
          </form>
        )}
      </div>
    </Modal>
  );
};
